import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, allArticles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'getting-started');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const articles = useMemo(
    () => allArticles.filter((a) => a.category === activeSlug),
    [activeSlug],
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 17c`,
    description: currentCategory.description,
    url: `https://17c.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://17c.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 17c内容分类`}
        description={currentCategory.description}
        keywords={`17c,${currentCategory.name},17c平台,17c使用教程,17c功能介绍`}
        canonicalUrl={`/category/${activeSlug}`}
        schemaJson={categorySchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-64 shrink-0">
              <div className="sticky top-24">
                <h2 className="text-sm font-semibold text-foreground-500 uppercase tracking-wide mb-4">
                  内容分类
                </h2>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => setActiveSlug(cat.slug)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all text-left cursor-pointer whitespace-nowrap ${
                        activeSlug === cat.slug
                          ? 'bg-primary-100 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <div className={`w-7 h-7 flex items-center justify-center rounded ${
                        activeSlug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className={`${cat.icon} text-sm`} />
                      </div>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-600 leading-relaxed">{currentCategory.description}</p>
              </div>

              <div className="space-y-5">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="flex flex-col sm:flex-row gap-5 p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="sm:w-48 h-32 shrink-0 rounded-md overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line w-3.5 h-3.5 flex items-center justify-center" />
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" />
                          {article.readTime}
                        </span>
                        <span>{article.publishDate}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-background-100 text-xs text-foreground-500 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}